import { useEffect, useState } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, AlertTriangle, ArrowUpRight, Clock, GraduationCap, Home, Users, DollarSign } from 'lucide-react';
import { trackEvent, getNeighbourhood } from '../lib/api';
import SEO from '../components/SEO';
import { GA_EVENTS } from '../lib/analytics';

function ScoreRing({ score, size = 64 }) {
  const radius = (size - 8) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 70 ? '#0ea58f' : score >= 40 ? '#eab308' : '#ef4444';
  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth="4" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
        />
      </svg>
      <span className="absolute font-mono text-base font-bold text-slate-900">{score}</span>
    </div>
  );
}

export default function MatchResults() {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [enriched, setEnriched] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('blueprint_results');
    if (!stored) { navigate('/blueprint'); return; }
    const parsed = JSON.parse(stored);
    setResults(parsed);
    trackEvent({ event_type: 'results_viewed', page: 'results' });
    GA_EVENTS.resultsViewed();

    // Fetch enriched data for top matches
    const slugs = (parsed.neighbourhood_scores || []).slice(0, 3).map(m => m.slug).filter(Boolean);
    slugs.forEach(slug => {
      getNeighbourhood(slug)
        .then(r => setEnriched(prev => ({ ...prev, [slug]: r.data })))
        .catch(() => {});
    });
  }, [navigate]);

  if (!results) return null;
  const { neighbourhood_scores = [], explanation } = results;

  return (
    <div className="min-h-screen mesh-gradient-light" data-testid="match-results-page">
      <SEO title="Your Burlington Neighbourhood Matches" noindex={true} />
      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        {/* Header */}
        <div className="mb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200/50 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            <span className="text-xs text-emerald-700 font-medium">Analysis complete</span>
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Your Burlington<br /><span className="italic">fit results</span>
          </h1>

          {explanation && (
            <p className="text-base text-slate-500 leading-relaxed mt-6 animate-fade-up delay-200" data-testid="match-explanation">
              {explanation}
            </p>
          )}
        </div>

        {/* Match Cards */}
        <div className="mb-14">
          {neighbourhood_scores.map((match, i) => {
            const detail = enriched[match.slug] || {};
            const hasDetail = Object.keys(detail).length > 0;

            return (
            <React.Fragment key={match.name}>
            <div
              className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden animate-fade-up mb-10"
              style={{ animationDelay: `${(i + 2) * 0.1}s` }}
              data-testid={`match-card-${match.slug || i}`}
            >
              {/* Card Header */}
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <ScoreRing score={match.score} size={56} />
                    <div>
                      <span className="font-mono text-[10px] text-slate-400">#{i + 1} Match</span>
                      <h3 className="text-2xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>
                        {match.name}
                      </h3>
                    </div>
                  </div>
                  <Link
                    to={`/neighbourhoods/${match.slug}`}
                    className="text-xs text-slate-400 hover:text-slate-900 transition-colors flex items-center gap-1 bg-slate-50 px-3 py-1.5 rounded-full"
                    data-testid={`view-guide-${match.slug || i}`}
                  >
                    Full guide <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>

                {match.vibe_summary && (
                  <p className="text-[14px] text-slate-500 leading-relaxed">{match.vibe_summary}</p>
                )}
              </div>

              {/* Quick Stats Bar */}
              {hasDetail && (
                <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-slate-100">
                  {/* Price */}
                  <div className="p-4 border-r border-slate-100 last:border-r-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <DollarSign className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">From</span>
                    </div>
                    <p className="font-mono text-sm font-semibold text-slate-900">
                      {formatPrice(detail.price_bands?.detached?.min || 0)}
                    </p>
                  </div>
                  {/* Commute */}
                  <div className="p-4 border-r border-slate-100 last:border-r-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">GO to Toronto</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900">
                      {detail.commute_times?.go_transit?.split('→')[1]?.trim() || '—'}
                    </p>
                  </div>
                  {/* Schools */}
                  <div className="p-4 border-r border-slate-100 last:border-r-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <GraduationCap className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Schools</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900">
                      {(detail.school_breakdown?.public?.length || 0) + (detail.school_breakdown?.catholic?.length || 0)} options
                    </p>
                  </div>
                  {/* Lots */}
                  <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Home className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Style</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {detail.architectural_styles?.split('.')[0]?.split(':')[0]?.substring(0, 30) || '—'}
                    </p>
                  </div>
                </div>
              )}

              {/* Why it fits + Tradeoffs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-slate-100">
                {match.reasons?.length > 0 && (
                  <div className="p-5 sm:border-r border-slate-100">
                    <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-widest mb-3">Why it fits</p>
                    <div className="space-y-2">
                      {match.reasons.map((r, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-[13px] text-slate-600">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {match.tradeoffs?.length > 0 && (
                  <div className="p-5 border-t sm:border-t-0 border-slate-100">
                    <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-widest mb-3">Tradeoffs</p>
                    <div className="space-y-2">
                      {match.tradeoffs.map((t, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-[13px] text-slate-600">{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Price breakdown */}
              {hasDetail && detail.price_bands && (
                <div className="border-t border-slate-100 p-5">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Price ranges in {match.name}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Detached', band: detail.price_bands?.detached },
                      { label: 'Town', band: detail.price_bands?.town },
                      { label: 'Condo', band: detail.price_bands?.condo },
                    ].map(item => (
                      <div key={item.label} className="text-center">
                        <p className="text-[10px] text-slate-400 mb-0.5">{item.label}</p>
                        {item.band?.min > 0 ? (
                          <p className="font-mono text-xs font-semibold text-slate-900">
                            {formatPriceRange(item.band.min, item.band.max)}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-300">N/A</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Best for */}
              {hasDetail && detail.best_for?.length > 0 && (
                <div className="border-t border-slate-100 p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <Users className="w-3.5 h-3.5 text-emerald-600" />
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Best for</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detail.best_for.slice(0, 3).map((item, j) => (
                      <span key={j} className="text-[11px] bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full border border-emerald-100/50">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* School highlights */}
              {hasDetail && detail.school_breakdown?.public?.length > 0 && (
                <div className="border-t border-slate-100 p-5">
                  <div className="flex items-center gap-1.5 mb-3">
                    <GraduationCap className="w-3.5 h-3.5 text-blue-600" />
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400">Key schools</p>
                  </div>
                  <div className="space-y-1.5">
                    {detail.school_breakdown.public.slice(0, 2).map((s, j) => (
                      <p key={j} className="text-[12px] text-slate-500 leading-relaxed">{s}</p>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2 italic">Catchment boundaries should be verified with the board.</p>
                </div>
              )}

              {/* Top FAQ */}
              {hasDetail && detail.faq?.length > 0 && (
                <div className="border-t border-slate-100 p-5 bg-slate-50/50">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-2">Common question</p>
                  <p className="text-[13px] font-medium text-slate-800 mb-1">{detail.faq[0].q}</p>
                  <p className="text-[12px] text-slate-500 leading-relaxed">{detail.faq[0].a}</p>
                </div>
              )}

              {/* Card footer */}
              <div className="border-t border-slate-100 p-4 flex items-center justify-between bg-slate-50/30">
                <Link
                  to={`/neighbourhoods/${match.slug}`}
                  className="text-[12px] text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1"
                >
                  Read the full {match.name} guide <ArrowRight className="w-3 h-3" />
                </Link>
                <Link to={`/homes-for-sale?neighbourhood=${match.slug}`}>
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-8 px-4 text-[11px] font-medium shadow-sm hover:-translate-y-px transition-all">
                    View homes for sale <ArrowRight className="ml-1 w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Inline CTA after first match */}
            {i === 0 && (
              <div className="bg-slate-950 rounded-2xl p-10 md:p-14 text-center noise-overlay relative overflow-hidden mb-10" data-testid="inline-results-cta">
                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl text-white tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    Your personalized brief is <span className="italic">on its way</span>
                  </h2>
                  <p className="text-sm text-white/40 mb-8 max-w-sm mx-auto">
                    A Burlington specialist will review your profile and reach out with tailored next steps. In the meantime, explore your matched areas.
                  </p>
                  <Link to="/homes-for-sale" data-testid="inline-results-cta-btn">
                    <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-3 h-auto text-sm font-semibold shadow-xl hover:-translate-y-px transition-all">
                      Browse Homes for Sale <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            </React.Fragment>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="bg-slate-950 rounded-2xl p-8 md:p-10 text-center noise-overlay relative overflow-hidden animate-fade-up delay-500" data-testid="results-cta-block">
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Explore your matched <span className="italic">neighbourhoods</span>
            </h2>
            <p className="text-sm text-white/40 mb-6 max-w-sm mx-auto">
              Dive into detailed guides, browse homes for sale, or revisit your results anytime.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/neighbourhoods">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-3 h-auto text-sm font-semibold shadow-xl hover:-translate-y-px transition-all">
                  Neighbourhood Guides <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/homes-for-sale">
                <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 rounded-full px-6 py-3 h-auto text-sm">
                  Browse Listings
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
